// =============================================================================
// AGC: DROID FARM - NDVI Trend Chart
// AGC: Line chart showing NDVI time series with trend analysis
// =============================================================================

import React, { useMemo, useRef, useEffect } from 'react'
import { TrendingUp, TrendingDown, Minus, Calendar } from 'lucide-react'
import { Chart, registerables } from 'chart.js'
import { useTranslation } from '../../../hooks/useTranslation'
import type { TrendAnalysisResult, FieldObservation, TrendDataPoint } from '../../../types'

// AGC: Register Chart.js components
Chart.register(...registerables)

interface NDVITrendChartProps {
  trend: TrendAnalysisResult | null
  observations?: FieldObservation[]
  dateRange?: '30' | '60' | '90'
  onDateRangeChange?: (range: '30' | '60' | '90') => void
  isLoading?: boolean
}

export const NDVITrendChart: React.FC<NDVITrendChartProps> = ({
  trend,
  observations = [],
  dateRange = '60',
  onDateRangeChange,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  const chartRef = useRef<HTMLCanvasElement>(null)
  const chartInstance = useRef<Chart | null>(null)

  // AGC: Prepare chart data
  const chartData = useMemo(() => {
    let dataPoints: TrendDataPoint[] = []

    if (trend?.timeSeries && trend.timeSeries.length > 0) {
      dataPoints = trend.timeSeries
    } else if (observations.length > 0) {
      // AGC: Fallback to observations if no trend data
      dataPoints = observations
        .filter(obs => obs.ndvi?.mean !== undefined)
        .map(obs => ({
          date: obs.date,
          value: obs.ndvi?.mean || 0,
          smoothed: obs.ndvi?.mean || 0,
          min: obs.ndvi?.p10 || 0,
          max: obs.ndvi?.p90 || 0,
        }))
        .reverse() // AGC: Oldest first for chart
    }

    // AGC: Filter by date range
    const now = new Date()
    const daysAgo = parseInt(dateRange)
    const cutoffDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000)
    
    return dataPoints.filter(d => new Date(d.date) >= cutoffDate)
  }, [trend, observations, dateRange])

  // AGC: Create/update chart
  useEffect(() => {
    if (!chartRef.current || chartData.length === 0) return

    // AGC: Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy()
    }

    const ctx = chartRef.current.getContext('2d')
    if (!ctx) return

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: chartData.map(d => {
          const date = new Date(d.date)
          return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        }),
        datasets: [
          {
            label: 'NDVI',
            data: chartData.map(d => d.value),
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 3,
            pointHoverRadius: 5,
          },
          {
            label: t('fields.monitoring.smoothed'),
            data: chartData.map(d => d.smoothed),
            borderColor: '#6366f1',
            borderDash: [5, 5],
            fill: false,
            tension: 0.4,
            pointRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index',
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 12,
              padding: 10,
              font: { size: 10 },
            },
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return `${context.dataset.label}: ${context.parsed.y.toFixed(3)}`
              },
            },
          },
        },
        scales: {
          y: {
            min: 0,
            max: 1,
            ticks: {
              stepSize: 0.2,
              font: { size: 10 },
            },
            title: {
              display: true,
              text: 'NDVI',
              font: { size: 10 },
            },
          },
          x: {
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              font: { size: 9 },
              maxTicksLimit: 10,
            },
          },
        },
      },
    })

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy()
        chartInstance.current = null
      }
    }
  }, [chartData, t])

  // AGC: Get trend info display
  const getTrendInfo = () => {
    if (!trend?.trend) return null

    const { direction, strength, slope, r2 } = trend.trend
    
    let icon
    let colorClass
    let text

    switch (direction) {
      case 'increasing':
        icon = <TrendingUp className="w-4 h-4" />
        colorClass = 'text-green-600 dark:text-green-400'
        text = strength === 'strong' 
          ? t('fields.monitoring.strongUptrend') 
          : t('fields.monitoring.uptrend')
        break
      case 'decreasing':
        icon = <TrendingDown className="w-4 h-4" />
        colorClass = 'text-red-600 dark:text-red-400'
        text = strength === 'strong'
          ? t('fields.monitoring.strongDowntrend')
          : t('fields.monitoring.downtrend')
        break
      default:
        icon = <Minus className="w-4 h-4" />
        colorClass = 'text-gray-600 dark:text-gray-400'
        text = t('fields.monitoring.stable')
    }

    return (
      <div className={`flex items-center gap-1.5 text-sm ${colorClass}`}>
        {icon}
        <span>{text}</span>
        {r2 > 0.5 && (
          <span className="text-xs text-gray-400">
            (R²={r2.toFixed(2)})
          </span>
        )}
      </div>
    )
  }

  // AGC: Loading state
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl p-4">
        <div className="animate-pulse">
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
          <div className="h-48 bg-gray-100 dark:bg-gray-700 rounded" />
        </div>
      </div>
    )
  }

  // AGC: No data state
  if (chartData.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('fields.monitoring.ndviTrend')}
          </h4>
        </div>
        <div className="h-48 flex items-center justify-center bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('fields.monitoring.noTrendData')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl p-4">
      {/* AGC: Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('fields.monitoring.ndviTrend')}
          </h4>
          {getTrendInfo()}
        </div>

        {/* AGC: Date Range Selector */}
        {onDateRangeChange && (
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
            {(['30', '60', '90'] as const).map((range) => (
              <button
                key={range}
                onClick={() => onDateRangeChange(range)}
                className={`px-2 py-1 text-xs rounded-md transition-colors ${
                  dateRange === range
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {range}d
              </button>
            ))}
          </div>
        )}
      </div>

      {/* AGC: Chart */}
      <div className="h-48">
        <canvas ref={chartRef} />
      </div>

      {/* AGC: Summary Stats */}
      {trend?.summary && (
        <div className="mt-3 grid grid-cols-4 gap-2 text-center">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('fields.monitoring.current')}
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {chartData[chartData.length - 1]?.value.toFixed(2) || '--'}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('fields.monitoring.average')}
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {trend.summary.mean.toFixed(2)}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('fields.monitoring.min')}
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {trend.summary.min.toFixed(2)}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('fields.monitoring.max')}
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {trend.summary.max.toFixed(2)}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default NDVITrendChart

